import { ReactNode, useEffect } from 'react';
import { PostHogProvider as PHProvider } from '@posthog/react';
import posthog from 'posthog-js';

interface PostHogProviderProps {
  children: ReactNode;
}

// Why a separate component rather than in AppProviders.tsx:
// PostHog requires posthog.init() to run only in the browser, not during SSR so we use useEffect to ensure this.
// Keeping it here avoids mixing browser-specific initialization logic with the pure provider nesting in AppProviders.
export const PostHogProvider = ({ children }: PostHogProviderProps) => {
  // Initialize PostHog when the component mounts.
  useEffect(() => {
    const posthogKey = process.env.NEXT_PUBLIC_POSTHOG_KEY;
    const posthogHost = process.env.NEXT_PUBLIC_POSTHOG_HOST;

    // Only initialize if both environment variables are set and PostHog hasn't been initialized yet.
    if (posthogKey && posthogHost && !posthog.__loaded) {
      try {
        posthog.init(posthogKey, {
          api_host: posthogHost,

          // Use modern PostHog defaults for better pageview tracking and metrics.
          defaults: '2025-11-30',

          // Only create person profiles when we explicitly call posthog.identify().
          person_profiles: 'identified_only',

          // Disable automatic capture of all clicks/form submissions.
          // Meaningful events can be tracked explicitly via posthog.capture().
          autocapture: false,

          // Don't use PostHog to capture exceptions as we have Sentry.
          capture_exceptions: false,
        });
      } catch (error) {
        console.error('PostHog initialization failed:', error);
      }
    }
  }, []);

  return <PHProvider client={posthog}>{children}</PHProvider>;
};
